import { useTranslation } from 'react-i18next';
import { notification } from 'antd';
import { reportClientError } from './clientErrorReport';

// The failures only the user's browser sees: no HTTP answer, or a page bug.
const REPORTED_KEYS = ['NETWORK_TIMEOUT', 'NETWORK_ERROR', 'UNEXPECTED'];

/**
 * Map what `dispatchAPI` rejects with to an `errors.message` key.
 *
 * An HTTP answer keeps its status. A request that got no answer is either a
 * timeout or a network failure (the server is unreachable, or a proxy error
 * without CORS headers). Anything else is a bug of the page itself and must
 * not pass for a slow server.
 * @param {Error|number|string} status - The rejection, or a bare status
 * @returns {number|string} The key under `errors.message`
 */
export const getErrorKey = (status) => {
  if (status?.response) return status.response.status;
  if (!(status instanceof Error)) return status;
  if (['ECONNABORTED', 'ETIMEDOUT'].includes(status.code)) {
    return 'NETWORK_TIMEOUT';
  }
  if (status.request) return 'NETWORK_ERROR';
  return 'UNEXPECTED';
};

export const useErrorMessage = () => {
  const { t } = useTranslation();
  return {
    message: (
      status = '404',
      duration = 4.5,
      key = undefined,
      notificationType = 'error'
    ) => {
      const s = getErrorKey(status);
      const d = status?.response?.data?.description;
      if (s === 'UNEXPECTED') {
        // Keep the trace of a page bug: the toast alone would hide it. An
        // axios error is summarised, its config carries the Authorization.
        // eslint-disable-next-line no-console
        console.error(status.isAxiosError ? status.toString() : status);
      }
      if (REPORTED_KEYS.includes(s)) reportClientError(status, s);
      if (s !== 498)
        notification[notificationType]({
          message: t(`errors.message.${d || s}`),
          key: key || 'network_feedback',
          duration
        });
    }
  };
};
